import axios, { type AxiosInstance, type AxiosResponse, type AxiosError } from 'axios';
import { API_BASE_URL, API_CONFIG } from '../utils/constants';

export interface ApiClientConfig {
  baseURL: string;
  timeout?: number;
}

export class ApiClient {
  private client: AxiosInstance;
  private config: Required<ApiClientConfig>;

  constructor(config: Partial<ApiClientConfig> = {}) {
    this.config = {
      baseURL: API_BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      ...config,
    };

    this.client = axios.create({
      baseURL: this.config.baseURL,
      headers: {
        'Accept': 'application/json',
      },
      withCredentials: false,
      timeout: this.config.timeout,
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    this.client.interceptors.request.use(
      (config) => {
        if (config.data instanceof FormData) {
          if (config.headers) {
            delete config.headers['Content-Type'];
          }
        } else if (config.data instanceof URLSearchParams || typeof config.data === 'string') {
          if (config.headers) {
            config.headers['Content-Type'] = 'application/x-www-form-urlencoded';
          }
        } else {
          if (config.headers) {
            config.headers['Content-Type'] = 'application/json';
          }
        }
        return config;
      }
    );

    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        console.error('API Error:', {
          status: error.response?.status,
          url: error.config?.url,
          message: error.message,
        });
        return Promise.reject(this.formatError(error));
      }
    );
  }

  private formatError(error: AxiosError): Error {
    if (error.message?.includes('CORS') || error.code === 'ERR_CORS') {
      return new Error('CORS Error: Cross-origin request blocked. Use proxy in development.');
    }

    if (error.response?.data) {
      const data = error.response.data as { 
        message?: string; 
        error?: string; 
        detail?: string | Array<{ msg?: string }> 
      };
      
      let message: string;
      
      if (typeof data.detail === 'string') {
        message = data.detail;
      } 
      else if (Array.isArray(data.detail) && data.detail.length > 0) {
        message = data.detail[0]?.msg || 'Unknown server error';
      }
      else {
        message = data.message || data.error || 'Unknown server error';
      }
      
      return new Error(message);
    }

    if (error.response?.status) {
      const statusMessages: Record<number, string> = {
        400: 'Bad Request',
        401: 'Unauthorized',
        403: 'Forbidden', 
        404: 'Not Found',
        429: 'Rate Limited',
        500: 'Internal Server Error',
        502: 'Bad Gateway',
        503: 'Service Unavailable'
      };
      const message = statusMessages[error.response.status] || `HTTP ${error.response.status}`;
      return new Error(message);
    }

    if (error.code === 'ECONNABORTED') return new Error('Request timeout');
    if (error.code === 'ERR_NETWORK') return new Error('Network error');
    
    return new Error(error.message || 'Unknown error');
  }

  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const response = await this.client.get<T>(url, { params });
    return response.data;
  }

  async post<T>(url: string, data?: unknown, signal?: AbortSignal): Promise<T> {
    const response = await this.client.post<T>(url, data, { signal });
    return response.data;
  }

  async postFormData<T>(url: string, formData: FormData, signal?: AbortSignal): Promise<T> {
    const response = await this.client.post<T>(url, formData, { 
      signal,
      // Не устанавливаем Content-Type - axios установит автоматически с правильным boundary
    });
    return response.data;
  }

  async postFormUrlEncoded<T>(url: string, data: Record<string, unknown>, signal?: AbortSignal): Promise<T> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(data)) {
      if (value !== null && value !== undefined) {
        params.append(key, String(value));
      }
    }
    const response = await this.client.post<T>(url, params.toString(), { 
      signal,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  }

  async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.put<T>(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.client.delete<T>(url);
    return response.data;
  }

  async patch<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.patch<T>(url, data);
    return response.data;
  }

  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    try {
      const endpoint = import.meta.env.DEV ? '/api/health' : '/api/health';
      const response = await this.get<{ status: string }>(endpoint);
      return {
        status: response.status || 'ok',
        timestamp: new Date().toISOString(),
      };
    } catch {
      return {
        status: 'error',
        timestamp: new Date().toISOString(),
      };
    }
  }

  getBaseURL(): string {
    return this.config.baseURL;
  }
}
